import { Table, Typography, theme } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Chip, type ChipVariant } from "components/ui";
import { useNavigate } from "react-router-dom";
import type { ITableBlock } from "./chat-block.types";
import { formatMoney } from "./format";

const { Text, Link } = Typography;

type Props = {
	block: ITableBlock;
};

type Row = Record<string, unknown> & { key: number };

function statusVariant(value: string): ChipVariant {
	const s = value.toLowerCase();
	// Check the negative states first — "unreconciled" contains "reconciled".
	if (
		/unreconciled|unmatched|pending|open|exception|failed|error|overdue/.test(s)
	)
		return "warning";
	if (/reconciled|matched|complete|done|success|paid|approved/.test(s))
		return "green";
	return "grey";
}

// Inline result table. AntD Table, small size, no pagination. Rows are already
// capped by the emitter; when `totalRows` exceeds them, a footer links to the
// full set in-app (drilldown). Money cells format via the MAJOR-units contract
// in format.ts — the renderer never does its own /100.
export function TableBlock({ block }: Props) {
	const { token } = theme.useToken();
	const navigate = useNavigate();

	const columns: ColumnsType<Row> = block.columns.map((col) => ({
		title: col.label,
		dataIndex: col.key,
		key: col.key,
		align: col.align ?? (col.kind === "money" ? "right" : "left"),
		render: (value: unknown) => {
			if (col.kind === "money") {
				return (
					<span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
						{formatMoney(value)}
					</span>
				);
			}
			if (col.kind === "status") {
				const label = value == null ? "" : String(value);
				return <Chip variant={statusVariant(label)}>{label}</Chip>;
			}
			return value == null ? "" : String(value);
		},
	}));

	const dataSource: Row[] = block.rows.map((row, i) => ({ key: i, ...row }));
	const hidden = block.totalRows - block.rows.length;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{block.title ? (
				<Text strong style={{ fontSize: 13 }}>
					{block.title}
				</Text>
			) : null}
			<Table<Row>
				size="small"
				pagination={false}
				columns={columns}
				dataSource={dataSource}
				scroll={{ x: "max-content" }}
			/>
			{hidden > 0 ? (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						fontSize: 12,
						color: token.colorTextSecondary,
					}}
				>
					<span>
						Showing {block.rows.length} of {block.totalRows}
					</span>
					{block.drilldown ? (
						<Link
							onClick={() => block.drilldown && navigate(block.drilldown.route)}
						>
							View all {block.totalRows} →
						</Link>
					) : null}
				</div>
			) : null}
		</div>
	);
}
